import type { ButtonProps } from '@mui/material';
import type { GridColDef, GridRowId, GridRowSelectionModel } from '@mui/x-data-grid-pro';
import type {
  PatientReadinessDetailsAppointment,
  PatientReadinessDetailsBucket,
  PatientReadinessDetailsCurrentBucket,
  PatientReadinessDetailsResponse,
  PatientReadinessDetailsSnapshotBucket,
  PatientReadinessRunBucket,
} from '#services/api/patient-readiness.ts';
import type { GetMeResponsePayload } from '#services/api/user.ts';

import * as React from 'react';
import { faSparkles } from '@fortawesome/pro-regular-svg-icons';
import { faCircleCheck as faCircleCheckRegular } from '@fortawesome/pro-regular-svg-icons';
import { faCircleCheck as faCircleCheckSolid } from '@fortawesome/pro-solid-svg-icons';
import { Dialog, FontAwesomeScalableIcon } from '@mend/mui';
import { Alert, Box, Button, Card, Container, Skeleton, Stack, Typography } from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { usePostHog } from '@posthog/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { format } from 'date-fns';
import { enqueueSnackbar } from 'notistack';
import { z } from 'zod';

import { useLocalState } from '#hooks/local-state.ts';
import { patientReadinessDetails } from '#lib/react-query/queries.ts';
import { createPatientReadinessRun } from '#services/api/patient-readiness.ts';

const highRiskLimit = 20;

const buckets = [
  {
    key: 'unconfirmed',
    label: () =>
      `Allow me to send confirmation requests to the ${highRiskLimit} highest-risk ${pluralize(highRiskLimit, 'appointment')}`,
    description:
      'Patients who confirm within 24 hours ahead of their appointment are 2x more likely to show',
    handledLabel: 'Confirmation requests sent',
    emptyLabel: 'All upcoming appointments are confirmed',
    emptyDescription: 'No confirmation work is needed right now',
    ctaLabel: `Show the top ${highRiskLimit}`,
  },
  {
    key: 'unreachable',
    label: (count: number) =>
      `${count} ${pluralize(count, 'appointment')} ${count === 1 ? 'has' : 'have'} unreachable patients, reconnect with them`,
    description: `Download a shareable report of upcoming appointments with unreachable patients`,
    handledLabel: 'Report downloaded',
    emptyLabel: 'All upcoming patients are reachable',
    emptyDescription: 'No report is needed right now',
    ctaLabel: 'View report',
  },
] as const;

const rate = 100;

interface SelectedUnconfirmedSnapshotBucket extends PatientReadinessDetailsSnapshotBucket<'unconfirmed'> {
  snapshotUnconfirmedCount: number;
}

interface SelectedPatientReadinessDetailsResponse extends Omit<
  PatientReadinessDetailsResponse,
  'unconfirmed'
> {
  unconfirmed:
    | PatientReadinessDetailsCurrentBucket<'unconfirmed'>
    | SelectedUnconfirmedSnapshotBucket;
}

function select(
  response: PatientReadinessDetailsResponse
): SelectedPatientReadinessDetailsResponse {
  const unconfirmed = response.unconfirmed;

  if (unconfirmed.source !== 'snapshot') {
    return { ...response, unconfirmed };
  }

  return {
    ...response,
    unconfirmed: {
      ...unconfirmed,
      snapshotUnconfirmedCount: unconfirmed.data.filter((appointment) => !appointment.confirmed)
        .length,
    },
  };
}

export function Readiness(props: { me: GetMeResponsePayload }) {
  const query = useQuery({ ...patientReadinessDetails, select });
  const posthog = usePostHog();
  const [selectedDetailsBucket, setSelectedDetailsBucket] =
    React.useState<PatientReadinessRunBucket | null>(null);

  React.useEffect(() => {
    posthog.capture('mia_readiness_tab_opened');
  }, [posthog]);

  return (
    <>
      <Container maxWidth="md">
        <Box
          display="grid"
          columnGap={2}
          rowGap={1}
          gridTemplateColumns="min-content 1fr"
          alignItems="center"
        >
          <Box
            padding={0.75}
            display="flex"
            alignItems="center"
            justifyContent="center"
            borderRadius="99px"
            bgcolor="quaternary.main"
            color="quaternary.contrastText"
          >
            <FontAwesomeScalableIcon icon={faSparkles} />
          </Box>

          <Stack alignItems="flex-end" spacing={0.5}>
            <Typography component="span" variant="subtitle2">
              Mia
            </Typography>
          </Stack>

          <Box gridColumn="2 / span 1">
            {query.isLoading ? (
              <Stack direction="column" spacing={2} width="100%">
                <Skeleton variant="rounded" height="25px" />
                <Skeleton variant="rounded" height="22px" />
                <Skeleton variant="rounded" height="80px" />
                <Skeleton variant="rounded" height="80px" />
              </Stack>
            ) : query.isError || !query.data ? (
              <Alert severity="error" variant="filled" sx={{ width: '100%' }}>
                Readiness summary could not load. Please try again later.
              </Alert>
            ) : (
              (() => {
                const unconfirmedCount =
                  query.data.unconfirmed.source === 'snapshot'
                    ? query.data.unconfirmed.snapshotUnconfirmedCount
                    : query.data.unconfirmed.data.length;
                const totalAtRiskCount = unconfirmedCount + query.data.unreachable.data.length;
                const hasAtRiskAppointments = totalAtRiskCount > 0;
                const recommendedActionsCount =
                  (query.data.unconfirmed.source === 'current' &&
                  query.data.unconfirmed.data.length > 0
                    ? 1
                    : 0) +
                  (query.data.unreachable.source === 'current' &&
                  query.data.unreachable.data.length > 0
                    ? 1
                    : 0);

                return (
                  <Stack direction="column" spacing={0}>
                    <Box mb={2}>
                      <Typography component="p" variant="subtitle1" mb={1}>
                        {hasAtRiskAppointments
                          ? `${unconfirmedCount} ${pluralize(unconfirmedCount, 'appointment')} ${pluralize(unconfirmedCount === 1 ? 2 : 1, 'remain')} unconfirmed in the next 2 days`
                          : 'No appointments are at risk in the next 2 days'}
                      </Typography>

                      <Typography component="p" variant="body1" color="text.secondary">
                        {hasAtRiskAppointments
                          ? 'I have identified high-risk appointments that would benefit most from outreach.'
                          : 'There are no readiness actions to take right now.'}
                      </Typography>
                    </Box>

                    {recommendedActionsCount > 0 && (
                      <Typography
                        component="p"
                        variant="body2"
                        textTransform="uppercase"
                        color="text.secondary"
                        fontWeight={600}
                        mb={1}
                      >
                        Recommended {pluralize(recommendedActionsCount, 'action')}:
                      </Typography>
                    )}

                    <Stack direction="column" spacing={1.5}>
                      {buckets.map((category) => {
                        const bucketDetails = query.data[category.key];
                        const count = bucketDetails.data.length;
                        const hasAppointments = count > 0;
                        const hasRun = bucketDetails.source === 'snapshot';

                        return (
                          <Card
                            key={category.key}
                            data-testid={`bucket-${category.key}`}
                            sx={{
                              padding: 2,
                              borderWidth: 1,
                              borderStyle: 'solid',
                              borderColor:
                                hasRun || !hasAppointments ? 'success.main' : 'transparent',
                            }}
                          >
                            <Stack
                              direction={{ xs: 'column', md: 'row' }}
                              alignItems={{ md: 'center' }}
                              justifyContent="space-between"
                              spacing={1}
                            >
                              <Stack direction="column" spacing={0.25} flexGrow={1}>
                                <Typography component="span" variant="body1" fontWeight={500}>
                                  {hasRun
                                    ? category.handledLabel
                                    : hasAppointments
                                      ? category.label(count)
                                      : category.emptyLabel}
                                  {hasAppointments && bucketDetails.source === 'snapshot' ? (
                                    <Typography
                                      component="span"
                                      variant="caption"
                                      color="success.main"
                                      sx={{ marginLeft: 0.5 }}
                                    >
                                      {`· Handled at ${format(new Date(bucketDetails.run.createdAt), 'h:mmaaa')}`}
                                    </Typography>
                                  ) : null}
                                </Typography>
                                <Typography
                                  component="span"
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {hasAppointments
                                    ? category.description
                                    : category.emptyDescription}
                                </Typography>
                              </Stack>

                              <Stack
                                flexShrink={0}
                                direction={{ xs: 'column', sm: 'row' }}
                                alignSelf={{ xs: 'stretch', sm: 'flex-end', md: 'center' }}
                              >
                                {hasAppointments ? (
                                  <Button
                                    color={!hasRun ? 'quaternary' : 'neutral'}
                                    variant={!hasRun ? 'contained' : 'text'}
                                    startIcon={
                                      !hasRun ? (
                                        <FontAwesomeScalableIcon icon={faSparkles} />
                                      ) : undefined
                                    }
                                    sx={{ flexShrink: 0 }}
                                    onClick={() => {
                                      posthog.capture('mia_readiness_tab_card_cta_clicked', {
                                        bucket: category.key,
                                        bucket_source: bucketDetails.source,
                                        appointment_count: count,
                                      });
                                      setSelectedDetailsBucket(category.key);
                                    }}
                                  >
                                    {!hasRun ? category.ctaLabel : 'View details'}
                                  </Button>
                                ) : null}
                              </Stack>
                            </Stack>
                          </Card>
                        );
                      })}
                    </Stack>
                  </Stack>
                );
              })()
            )}
          </Box>
        </Box>
      </Container>

      {query.data && selectedDetailsBucket ? (
        <ReadinessDetailsDialog
          me={props.me}
          bucket={selectedDetailsBucket}
          bucketDetails={query.data[selectedDetailsBucket]}
          asOf={query.dataUpdatedAt ? new Date(query.dataUpdatedAt).toISOString() : undefined}
          onClose={() => setSelectedDetailsBucket(null)}
        />
      ) : null}
    </>
  );
}

interface ReadinessCtaButtonBaseProps {
  label: string;
  appointmentCount: number;
  asOf?: string;
  size?: ButtonProps['size'];
  onHandled?: () => void;
}

interface UnconfirmedReadinessCtaButtonProps extends ReadinessCtaButtonBaseProps {
  bucket: 'unconfirmed';
  getSelectedAppointmentIds: () => number[];
}

interface UnreachableReadinessCtaButtonProps extends ReadinessCtaButtonBaseProps {
  bucket: 'unreachable';
  appointments: PatientReadinessDetailsAppointment<'unreachable'>[];
}

type ReadinessCtaButtonProps =
  | UnconfirmedReadinessCtaButtonProps
  | UnreachableReadinessCtaButtonProps;

function ReadinessCtaButton(props: ReadinessCtaButtonProps) {
  const queryClient = useQueryClient();
  const posthog = usePostHog();
  const createRunMutation = useMutation({
    mutationFn: createPatientReadinessRun,
    onSuccess: (response, variables) => {
      const patientReadinessDetailsUpdatedAt = queryClient.getQueryState(
        patientReadinessDetails.queryKey
      )?.dataUpdatedAt;

      queryClient.setQueryData<PatientReadinessDetailsResponse>(
        patientReadinessDetails.queryKey,
        (currentData) => {
          if (!currentData) {
            return currentData;
          }

          return {
            ...currentData,
            [variables.bucket]: {
              ...currentData[variables.bucket],
              source: 'snapshot',
              run: response.data.run,
              data:
                variables.bucket === 'unconfirmed'
                  ? currentData.unconfirmed.data.filter((appointment) =>
                      variables.appointmentIds.includes(appointment.id)
                    )
                  : currentData.unreachable.data,
            },
          };
        },
        patientReadinessDetailsUpdatedAt
          ? { updatedAt: patientReadinessDetailsUpdatedAt }
          : undefined
      );
      props.onHandled?.();
    },
    onError: (error) => {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        enqueueSnackbar('Mia is already working on this readiness bucket.', { variant: 'error' });
        return;
      }

      enqueueSnackbar('Mia could not start this work. Please try again later.', {
        variant: 'error',
      });
    },
  });

  function performAction() {
    if (props.bucket === 'unreachable') {
      posthog.capture('mia_readiness_details_dialog_cta_clicked', {
        bucket: 'unreachable',
        appointment_count: props.appointmentCount,
        selected_appointment_count: null,
      });
      downloadUnreachablePatientsCsv(props.appointments);
      createRunMutation.mutate({ bucket: 'unreachable', asOf: props.asOf });
      return;
    }

    const appointmentIds = props.getSelectedAppointmentIds();

    if (appointmentIds.length === 0) {
      enqueueSnackbar('Select at least one appointment for Mia to confirm.', { variant: 'error' });
      return;
    }

    posthog.capture('mia_readiness_details_dialog_cta_clicked', {
      bucket: 'unconfirmed',
      appointment_count: props.appointmentCount,
      selected_appointment_count: appointmentIds.length,
    });
    createRunMutation.mutate({ bucket: 'unconfirmed', asOf: props.asOf, appointmentIds });
  }

  return (
    <Button
      color="quaternary"
      startIcon={<FontAwesomeScalableIcon icon={faSparkles} />}
      sx={{ flexShrink: 0 }}
      loading={createRunMutation.isPending}
      loadingPosition="start"
      disabled={createRunMutation.isPending}
      size={props.size ?? 'medium'}
      onClick={performAction}
    >
      {props.label}
    </Button>
  );
}

function downloadUnreachablePatientsCsv(
  appointments: PatientReadinessDetailsAppointment<'unreachable'>[]
) {
  const rows = appointments.map((appointment) => [
    formatPersonName(appointment.patient),
    format(new Date(appointment.startDate), 'LLL d, h:mmaaa'),
    formatPersonName(appointment.provider),
    appointment.appointmentType.name,
    `$${rate}`,
  ]);
  const csv = [['Patient', 'Appointment', 'Provider', 'Type', 'Revenue'], ...rows]
    .map((row) => row.map((value) => `"${value.replace(/"/g, '""')}"`).join(','))
    .join('\r\n');
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const anchor = document.createElement('a');

  anchor.href = url;
  anchor.download = 'mia-unreachable.csv';
  anchor.click();

  // Defer cleanup so the browser can consume the object URL after the click
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

function pluralize(count: number, singular: string) {
  return count === 1 ? singular : `${singular}s`;
}

type AnyPatientReadinessDetailsAppointment =
  | PatientReadinessDetailsAppointment<'unconfirmed'>
  | PatientReadinessDetailsAppointment<'unreachable'>;

const columns: GridColDef<AnyPatientReadinessDetailsAppointment>[] = [
  {
    field: 'patientName',
    headerName: 'Patient',
    valueGetter: (_value, row) => formatPersonName(row.patient),
  },
  {
    field: 'appointmentDate',
    headerName: 'Appointment',
    valueGetter: (_value, row) => new Date(row.startDate),
    valueFormatter: (value: Date) => format(value, 'LLL d, h:mmaaa'),
  },
  {
    field: 'confirmed',
    headerName: 'Confirmed',
    minWidth: 180,
    valueGetter: (_value, row) => (row.confirmed ? new Date(row.confirmed) : null),
    valueFormatter: (value: Date | null) => (value ? format(value, 'LLL d, h:mmaaa') : 'Pending'),
    renderCell: (params) =>
      params.value ? (
        <Stack direction="row" alignItems="center" spacing={0.75} component="span">
          <FontAwesomeScalableIcon icon={faCircleCheckSolid} sx={{ color: 'success.main' }} />
          <Typography variant="inherit" color="success.main">
            {params.formattedValue}
          </Typography>
        </Stack>
      ) : (
        <Typography variant="inherit" color="text.secondary">
          Pending
        </Typography>
      ),
  },
  {
    field: 'providerName',
    headerName: 'Provider',
    valueGetter: (_value, row) => formatPersonName(row.provider),
  },
  {
    field: 'appointmentType',
    headerName: 'Type',
    valueGetter: (_value, row) => row.appointmentType.name,
  },
  {
    field: 'attendanceRate',
    headerName: 'Risk Reason',
    sortable: false,
    valueGetter: (_value, row) => {
      const history = 'history' in row.patient ? row.patient.history : null;
      if (!history || history.totalAppointmentsInWindow === 0) return 0;
      return (history.noShowCount + history.lateCancelCount) / history.totalAppointmentsInWindow;
    },
    valueFormatter: (_value, row) => {
      if (!('history' in row.patient)) return '';
      return [
        row.patient.history.noShowCount > 0 ? 'Historical No Show' : null,
        row.patient.history.lateCancelCount > 0 ? 'Historical Late Cancel' : null,
        row.patient.history.isNewPatient ? 'New Patient' : null,
        'Unconfirmed',
      ]
        .filter(Boolean)
        .join(', ');
    },
  },
];

function formatPersonName(person: { firstName?: string; lastName?: string }) {
  const name = [person.firstName, person.lastName].filter(Boolean).join(' ');
  return name || '--';
}

function Placeholder(props: { children: string; color?: string }) {
  return (
    <Typography component="span" variant="inherit" color="inherit">
      {'{'}
      <Typography component="span" variant="inherit" color={props.color ?? 'text.primary'}>
        {props.children}
      </Typography>
      {'}'}
    </Typography>
  );
}

const hasJoinedWaitlistSchema = z.boolean();

const miaProPerks = [
  {
    title: 'Unlimited confirmations',
    description: `Automatically confirm care with every appointment, not just top ${highRiskLimit}`,
  },
  {
    title: 'Daily digest',
    description: 'An email highlighting your biggest risks and what Mia can do about it',
  },
  {
    title: 'Priority risk scoring',
    description: "Advanced model trained on your organization's no-show history",
  },
  {
    title: 'Custom branding',
    description:
      'Customize your Care Coordinator to match the tone and uniqueness of your organization',
  },
] as const;

/**
 * Early Access Feature key from PostHog (Early access features → "Readiness agent actions").
 * Joining the waitlist enrolls the user in this feature.
 */
const readinessAgentActionsFeatureKey = 'readiness-agent-actions';

function ReadinessDetailsDialog<TBucket extends PatientReadinessRunBucket>(props: {
  me: GetMeResponsePayload;
  bucket: TBucket;
  bucketDetails: PatientReadinessDetailsBucket<TBucket>;
  asOf?: string;
  onClose: () => void;
}) {
  const titleId = React.useId();
  const waitlistTitleId = React.useId();
  const waitlistDescriptionId = React.useId();

  const [initialRowSelectionModel] = React.useState<GridRowSelectionModel>(() => ({
    type: 'include',
    ids: new Set(props.bucketDetails.data.slice(0, highRiskLimit).map((row) => row.id)),
  }));
  const [rowSelectionModel, setRowSelectionModel] = React.useState(initialRowSelectionModel);

  const [isWaitlistDialogOpen, setIsWaitlistDialogOpen] = React.useState(false);
  const [hasJoinedWaitlist, setHasJoinedWaitlist] = useLocalState({
    key: `mia-readiness-actions-waitlist-${props.me.user.id}-${props.me.org.id}`,
    defaultValue: false,
    schema: hasJoinedWaitlistSchema,
  });

  const posthog = usePostHog();

  function handleRowSelectionModelChange(nextSelectionModel: GridRowSelectionModel) {
    if (hasJoinedWaitlist) {
      setRowSelectionModel(nextSelectionModel);
      return;
    }

    const normalizedSelectedGridRowIds =
      nextSelectionModel.type === 'include'
        ? nextSelectionModel.ids
        : new Set(
            props.bucketDetails.data
              .filter((row) => !nextSelectionModel.ids.has(row.id))
              .map((row) => row.id)
          );

    const selectionsByGroup = Array.from(normalizedSelectedGridRowIds).reduce(
      (groups, rowId) => {
        if (initialRowSelectionModel.ids.has(rowId)) {
          groups.initial.add(rowId);
        } else {
          groups.extra.add(rowId);
        }
        return groups;
      },
      { initial: new Set<GridRowId>(), extra: new Set<GridRowId>() }
    );

    if (selectionsByGroup.extra.size > 0) {
      posthog.capture('mia_readiness_waitlist_dialog_opened', {
        bucket: props.bucket,
        appointment_count: props.bucketDetails.data.length,
        selected_appointment_count: normalizedSelectedGridRowIds.size,
      });
      setIsWaitlistDialogOpen(true);
      return;
    }

    setRowSelectionModel({ type: 'include', ids: selectionsByGroup.initial });
  }

  function closeWaitlistDialog() {
    setIsWaitlistDialogOpen(false);
  }

  function stayOnFree() {
    const selectedAppointmentCount =
      props.bucket === 'unconfirmed'
        ? rowSelectionModel.type === 'include'
          ? rowSelectionModel.ids.size
          : props.bucketDetails.data.length - rowSelectionModel.ids.size
        : null;
    posthog.capture('mia_readiness_waitlist_dialog_stay_on_free_clicked', {
      bucket: props.bucket,
      appointment_count: props.bucketDetails.data.length,
      selected_appointment_count: selectedAppointmentCount,
    });
    closeWaitlistDialog();
  }

  function joinWaitlist() {
    // Optimistic: flip immediately and fire the PostHog calls in the background. Enrollment is
    // best-effort (client-side, ad-blockable) and we don't gate the UI on it.
    setHasJoinedWaitlist(true);
    enqueueSnackbar(
      "Congrats, you've joined the waitlist for Mia Pro! We will reach out to you via email to discuss next steps. In the meantime, you can now reach out to unlimited patients.",
      { variant: 'success' }
    );

    posthog.updateEarlyAccessFeatureEnrollment(readinessAgentActionsFeatureKey, true);

    closeWaitlistDialog();
  }

  const isRowSelectionEnabled =
    props.bucket === 'unconfirmed' && props.bucketDetails.source === 'current';

  return (
    <>
      <Dialog.Root
        open
        onClose={props.onClose}
        fullWidth
        fullHeight
        maxWidth="md"
        aria-labelledby={titleId}
      >
        <Dialog.Header>
          <Dialog.Title id={titleId}>
            {props.bucket === 'unconfirmed'
              ? `Top ${highRiskLimit} High-Risk Appointments`
              : 'Unreachable Patients'}
          </Dialog.Title>
        </Dialog.Header>

        <Dialog.Content>
          <Stack direction="column" spacing={2} height="100%" minHeight={0}>
            {props.bucket === 'unconfirmed' ? (
              <Card sx={{ padding: 2, flexShrink: 0 }}>
                <Stack direction="column" spacing={1}>
                  <Typography component="p" variant="body1">
                    Example message:
                  </Typography>
                  <Typography
                    component="p"
                    variant="body2"
                    color="text.secondary"
                    fontStyle="italic"
                  >
                    Hi, <Placeholder>name</Placeholder>
                    <br />
                    I&apos;m Mia, a care coordinator with {props.me.org.name}. I noticed you
                    haven&apos;t confirmed your appointment with <Placeholder>provider</Placeholder>{' '}
                    on <Placeholder>day</Placeholder> at <Placeholder>time</Placeholder>. Will you
                    be there? Please click this link to confirm: <Placeholder>link</Placeholder>
                  </Typography>
                </Stack>
              </Card>
            ) : null}

            <DataGridPro
              rows={props.bucketDetails.data as AnyPatientReadinessDetailsAppointment[]}
              columns={columns}
              hideFooter
              showToolbar
              checkboxSelection={isRowSelectionEnabled}
              disableRowSelectionOnClick
              rowSelectionModel={isRowSelectionEnabled ? rowSelectionModel : undefined}
              onRowSelectionModelChange={
                isRowSelectionEnabled ? handleRowSelectionModelChange : undefined
              }
              disableColumnSelector
              disableColumnFilter
              autosizeOnMount
              autosizeOptions={{ expand: true }}
              sx={{ flex: 1, minHeight: 0 }}
              initialState={{
                sorting: {
                  sortModel:
                    props.bucket === 'unconfirmed'
                      ? [
                          { field: 'attendanceRate', sort: 'desc' },
                          { field: 'appointmentDate', sort: 'asc' },
                        ]
                      : [{ field: 'appointmentDate', sort: 'asc' }],
                },
                columns: {
                  columnVisibilityModel: {
                    attendanceRate: props.bucket === 'unconfirmed',
                    confirmed:
                      props.bucket === 'unconfirmed' && props.bucketDetails.source === 'snapshot',
                  },
                },
              }}
              slotProps={{
                toolbar: {
                  csvOptions: { fileName: `mia-${props.bucket}` },
                  printOptions: { disableToolbarButton: true },
                },
              }}
            />
          </Stack>
        </Dialog.Content>

        <Dialog.Footer>
          <Dialog.DismissButton />

          {props.bucketDetails.source === 'current' ? (
            props.bucket === 'unreachable' ? (
              <ReadinessCtaButton
                bucket="unreachable"
                label="Download report"
                appointmentCount={props.bucketDetails.data.length}
                asOf={props.asOf}
                size="large"
                onHandled={props.onClose}
                appointments={
                  props.bucketDetails.data as PatientReadinessDetailsAppointment<'unreachable'>[]
                }
              />
            ) : (
              <ReadinessCtaButton
                bucket="unconfirmed"
                label="Confirm for me"
                appointmentCount={props.bucketDetails.data.length}
                getSelectedAppointmentIds={() =>
                  props.bucketDetails.data
                    .filter((appointment) =>
                      rowSelectionModel.type === 'include'
                        ? rowSelectionModel.ids.has(appointment.id)
                        : !rowSelectionModel.ids.has(appointment.id)
                    )
                    .map((appointment) => appointment.id)
                }
                asOf={props.asOf}
                size="large"
                onHandled={props.onClose}
              />
            )
          ) : null}
        </Dialog.Footer>
      </Dialog.Root>

      {isWaitlistDialogOpen && (
        <Dialog.Root
          open
          onClose={closeWaitlistDialog}
          maxWidth="sm"
          fullWidth
          aria-labelledby={waitlistTitleId}
          aria-describedby={waitlistDescriptionId}
          slotProps={{
            paper: { sx: { borderWidth: 2, borderStyle: 'solid', borderColor: 'quaternary.main' } },
          }}
        >
          <Dialog.Header>
            <Dialog.Title id={waitlistTitleId}>Upgrade to Mia Pro</Dialog.Title>
            <Dialog.Description id={waitlistDescriptionId}>
              Your current plan includes outreach for up to {highRiskLimit} high-risk appointments
              per day.{' '}
              <Typography component="span" variant="inherit" fontWeight={500}>
                Upgrade to let Mia automatically reduce no shows for you
              </Typography>
              .
            </Dialog.Description>
          </Dialog.Header>

          <Dialog.Content>
            <Card component={Stack} direction="column" spacing={1.5} padding={2} marginBottom={2}>
              {miaProPerks.map((perk) => (
                <Box
                  key={perk.title}
                  display="grid"
                  gridTemplateColumns="min-content 1fr"
                  columnGap={1.5}
                  alignItems="center"
                >
                  <FontAwesomeScalableIcon icon={faCircleCheckRegular} color="success" />

                  <Typography variant="body1">{perk.title}</Typography>
                  <Typography variant="body2" color="text.secondary" gridColumn="2 / span 1">
                    {perk.description}
                  </Typography>
                </Box>
              ))}
            </Card>

            <Card sx={{ padding: 2 }}>
              <Typography variant="body2" color="text.secondary">
                <Typography component="span" color="quaternary.main" variant="inherit">
                  Average Impact:
                </Typography>{' '}
                Patients who confirm within 24 hours ahead of their appointment are{' '}
                <Typography component="span" variant="inherit" fontWeight={500}>
                  2x more likely to show
                </Typography>
                .
              </Typography>
            </Card>
          </Dialog.Content>

          <Dialog.Footer>
            <Button color="neutral" variant="outlined" size="large" onClick={stayOnFree}>
              Stay on free
            </Button>

            <Button
              variant="contained"
              color="quaternary"
              size="large"
              startIcon={<FontAwesomeScalableIcon icon={faSparkles} />}
              onClick={joinWaitlist}
            >
              Join the waitlist
            </Button>
          </Dialog.Footer>
        </Dialog.Root>
      )}
    </>
  );
}
